import { clone } from "@helpers/object.helper";
import {
	Actioner,
	BuyFlowActionKeys,
	RegisterActionKeys,
} from "@services/actioner";
import { reportError } from "@utils/errorReporting";

const listeners = [];

const brazeEventkeys = [
	RegisterActionKeys.COMPLETE_REMINDME,
	RegisterActionKeys.COMPLETE_REMINDME_FAILED,
	RegisterActionKeys.COMPLETE_FRIENDS_FAMILY,
	RegisterActionKeys.COMPLETE_LEAD_COLLECTION_FORM,
	BuyFlowActionKeys.BUY_SET_PLAN,
	BuyFlowActionKeys.BUY_PLAN_NAME,
	BuyFlowActionKeys.BUY_IDD,
	BuyFlowActionKeys.BUY_PROMO_CODE,
	BuyFlowActionKeys.BUY_SET_CURRENT_NUMBER,
	BuyFlowActionKeys.BUY_SET_DOB,
	BuyFlowActionKeys.BUY_SET_RESIDENTIAL_ADDRESS,
	BuyFlowActionKeys.BUY_SET_ACCOUNT_ID,
	BuyFlowActionKeys.BUY_SET_EMAIL,
	BuyFlowActionKeys.BUY_SUCCESS,
	BuyFlowActionKeys.BUY_SET_SIM_TYPE,
	BuyFlowActionKeys.BUY_SET_SIM_CHANNEL,
	BuyFlowActionKeys.BUY_SET_SIM_CHANNEL_EVENT,
	BuyFlowActionKeys.RETAIL_SIM_CONFIRMED,
	BuyFlowActionKeys.BUY_SET_BRAZE_ID,
	BuyFlowActionKeys.BUY_SET_FIRST_NAME,
	BuyFlowActionKeys.BUY_SET_LAST_NAME,
];

const Braze = {
	start: () => {
		if (!window || !window.braze) {
			reportError(
				new Error("Braze could not start due to being uninitialized"),
				{
					route:
						typeof window !== "undefined"
							? window.location.pathname
							: undefined,
					flowStep: window?.currentBuyFlowStep,
					component: "Braze",
					action: "start",
				},
			);
			return;
		}

		// biome-ignore lint/complexity/noForEach: <explanation>
		brazeEventkeys.forEach((eventKey) => {
			listeners.push(
				Actioner.addListener(eventKey, (payload) => {
					const key = clone(payload.actionKey);
					const eventData = clone(payload.eventData);
					const setUserBraze = (id) => window.braze.changeUser(id);

					const setNameAttributesBraze = (data) => {
						window.braze.getUser().setFirstName(data.firstName);
						window.braze.getUser().setLastName(data.lastName);
					};

					const setEmailAttributeBraze = (data) =>
						window.braze.getUser().setEmail(data.email);

					const setDateForReminder = (data) => {
						window.braze
							.getUser()
							.setCustomUserAttribute(
								"date_for_reminder",
								new Date(data.dateForReminder),
							);
					};

					const setLeadSource = (source) =>
						window.braze
							.getUser()
							.setCustomUserAttribute("lead_source", source);

					try {
						if (key.label === Actioner.keyDefs.COMPLETE_REMINDME.label) {
							setNameAttributesBraze(eventData);
							setEmailAttributeBraze(eventData);
							window.braze.logCustomEvent(
								RegisterActionKeys.COMPLETE_REMINDME.label,
							);
							setDateForReminder(eventData);
							setLeadSource("remind-me-later");
						}
					} catch (e) {
						Actioner.trigger(Actioner.keyDefs.ERROR, e);
					}

					try {
						if (key.label === Actioner.keyDefs.BUY_PLAN_NAME.label) {
							const data = clone(payload);
							window.braze
								.getUser()
								.setCustomUserAttribute("buy_plan_name", data.plan_name);
						}
					} catch (e) {
						Actioner.trigger(Actioner.keyDefs.ERROR, e);
					}

					try {
						if (key.label === Actioner.keyDefs.BUY_IDD.label) {
							const data = clone(payload);
							window.braze
								.getUser()
								.setCustomUserAttribute("Buy_IDD", data.idd_selected);
						}
					} catch (e) {
						Actioner.trigger(Actioner.keyDefs.ERROR, e);
					}

					try {
						if (key.label === Actioner.keyDefs.BUY_PROMO_CODE.label) {
							const data = clone(payload);
							window.braze
								.getUser()
								.setCustomUserAttribute("Buy_promo_code", data.promo_code_name);
						}
					} catch (e) {
						Actioner.trigger(Actioner.keyDefs.ERROR, e);
					}

					try {
						if (key.label === Actioner.keyDefs.BUY_SET_PLAN.label) {
							const data = clone(payload);

							window.braze.logCustomEvent("SELECT_PLAN", {
								plan_name: data.plan_name,
								data_inclusion: data.data_inclusion,
								prices: data.prices,
							});
							window.braze
								.getUser()
								.setCustomUserAttribute("Buy_IDD", data.addingIDD);
							window.braze
								.getUser()
								.setCustomUserAttribute(
									"Buy_promo_code",
									data.PromoCodeEntered,
								);
							window.braze
								.getUser()
								.setCustomUserAttribute("porting", data.porting);
						}
					} catch (e) {
						Actioner.trigger(Actioner.keyDefs.ERROR, e);
					}

					try {
						if (key.label === Actioner.keyDefs.COMPLETE_FRIENDS_FAMILY.label) {
							setNameAttributesBraze(eventData);
							setEmailAttributeBraze(eventData);
							window.braze.logCustomEvent(
								RegisterActionKeys.COMPLETE_FRIENDS_FAMILY.label,
								{
									employee_referrer_state: eventData.employeeState,
									employee_referrer_id: eventData.employeeId,
								},
							);

							setLeadSource("FamilyandFriends");
							window.braze
								.getUser()
								.setCustomUserAttribute(
									"employee_referrer_id",
									eventData.employeeId,
								);
							window.braze
								.getUser()
								.setCustomUserAttribute(
									"employee_referrer_state",
									eventData.employeeState,
								);
						}
					} catch (e) {
						Actioner.trigger(Actioner.keyDefs.ERROR, e);
					}

					try {
						if (
							key.label === Actioner.keyDefs.COMPLETE_LEAD_COLLECTION_FORM.label
						) {
							setNameAttributesBraze(eventData);
							setEmailAttributeBraze(eventData);
							window.braze.logCustomEvent(
								RegisterActionKeys.COMPLETE_LEAD_COLLECTION_FORM.label,
								{
									lead_capture_form: eventData.formName,
								},
							);

							setLeadSource(eventData.formName);
						}
					} catch (e) {
						Actioner.trigger(Actioner.keyDefs.ERROR, e);
					}

					try {
						if (key.label === BuyFlowActionKeys.BUY_SET_CURRENT_NUMBER.label) {
							const formatted = `+61${`${eventData}`.substring(1)}`;
							window.braze.getUser().setPhoneNumber(formatted);
						}
					} catch (e) {
						Actioner.trigger(Actioner.keyDefs.ERROR, e);
					}

					try {
						if (key.label === BuyFlowActionKeys.BUY_SET_FIRST_NAME.label) {
							window.braze.getUser().setFirstName(eventData);
						}
					} catch (e) {
						Actioner.trigger(Actioner.keyDefs.ERROR, e);
					}

					try {
						if (key.label === BuyFlowActionKeys.BUY_SET_LAST_NAME.label) {
							window.braze.getUser().setLastName(eventData);
						}
					} catch (e) {
						Actioner.trigger(Actioner.keyDefs.ERROR, e);
					}

					try {
						if (key.label === BuyFlowActionKeys.BUY_SET_DOB.label) {
							const formatedDOB = eventData.split("/");
							const day = formatedDOB[0];
							const month = formatedDOB[1];

							window.braze.getUser().setDateOfBirth(1900, month, day);
						}
					} catch (e) {
						Actioner.trigger(Actioner.keyDefs.ERROR, e);
					}

					try {
						if (
							key.label === BuyFlowActionKeys.BUY_SET_RESIDENTIAL_ADDRESS.label
						) {
							window.braze
								.getUser()
								.setCustomUserAttribute("billing_postcode", eventData);
						}
					} catch (e) {
						Actioner.trigger(Actioner.keyDefs.ERROR, e);
					}

					try {
						if (key.label === BuyFlowActionKeys.BUY_SET_ACCOUNT_ID.label) {
							const accountId = clone(payload.value);
							window.braze.logCustomEvent("ACCOUNT_CREATED");
							window.braze.getUser().addAlias(`${accountId}`, "MRTM_ID");
						}
					} catch (e) {
						Actioner.trigger(Actioner.keyDefs.ERROR, e);
					}

					try {
						if (key.label === BuyFlowActionKeys.BUY_SET_EMAIL.label) {
							const data = clone(payload);
							if (data.current_plan) {
								window.braze
									.getUser()
									.setCustomUserAttribute("buy_plan_name", data.current_plan);
							}
							setEmailAttributeBraze(data);
							window.braze.logCustomEvent(
								BuyFlowActionKeys.BUY_SET_EMAIL.label,
							);
							window.braze
								.getUser()
								.setCustomUserAttribute("buy_flow", data.buy_flow);
							window.braze
								.getUser()
								.setCustomUserAttribute("lead_source", data.buy_flow);
							window.braze
								.getUser()
								.setCustomUserAttribute("updated_email", data.email);
						}
					} catch (e) {
						Actioner.trigger(Actioner.keyDefs.ERROR, e);
					}

					try {
						if (key.label === BuyFlowActionKeys.BUY_SET_BRAZE_ID.label) {
							const externalId = clone(payload.externalId);
							setUserBraze(externalId);
						}
					} catch (e) {
						Actioner.trigger(Actioner.keyDefs.ERROR, e);
					}

					try {
						if (key.label === BuyFlowActionKeys.BUY_SET_SIM_TYPE.label) {
							const simType = clone(payload.simType);
							window.braze
								.getUser()
								.setCustomUserAttribute("sim_type", simType);
						}
					} catch (error) {
						Actioner.trigger(Actioner.keyDefs.ERROR, error);
					}
					try {
						if (key.label === BuyFlowActionKeys.BUY_SET_SIM_CHANNEL.label) {
							const simChannel = clone(payload.simChannel);
							window.braze
								.getUser()
								.setCustomUserAttribute("sim_channel", simChannel);
						}
					} catch (error) {
						Actioner.trigger(Actioner.keyDefs.ERROR, error);
					}

					try {
						if (
							key.label === BuyFlowActionKeys.BUY_SET_SIM_CHANNEL_EVENT.label
						) {
							const simChannel = clone(payload.simChannel);
							window.braze.logCustomEvent(simChannel);
						}
					} catch (error) {
						Actioner.trigger(Actioner.keyDefs.ERROR, error);
					}
					try {
						if (key.label === BuyFlowActionKeys.RETAIL_SIM_CONFIRMED.label) {
							window.braze.logCustomEvent("RETAIL_SIM_CONFIRMED");
						}
					} catch (error) {
						Actioner.trigger(Actioner.keyDefs.ERROR, error);
					}

					window.braze.requestImmediateDataFlush();
				}),
			);
		});
	},

	stop: () => {
		// biome-ignore lint/complexity/noForEach: <explanation>
		listeners.forEach((listener) => {
			listener.remove();
		});
	},
};

export default Braze;
